import { DayFlag, getDefaultClassNames, SelectionState, UI } from 'react-day-picker'

const defaultClassNames = getDefaultClassNames()

/**
 * Appends our own stable class name alongside react-day-picker's default one (which keeps its base
 * stylesheet, imported for structural/spacing rules, working) so our styling hooks in {@link CALENDAR_SELECTORS}
 * don't depend on the library's internal `.rdp-*` naming, which is unlikely but not guaranteed to survive a version bump
 */
const withOwnClass = (uiElement: UI | DayFlag | SelectionState, ownClass: string): string => `${defaultClassNames[uiElement]} ${ownClass}`

/**
 * The single source of truth for our own `cal-*` class names, referenced by both {@link CALENDAR_CLASS_NAMES}
 * and {@link CALENDAR_SELECTORS} so the two can never drift apart.
 */
const CLASSES = {
    ROOT: 'cal-root',
    DAY: 'cal-day',
    DAY_BUTTON: 'cal-day-button',
    MONTH_CAPTION: 'cal-month-caption',
    CAPTION_LABEL: 'cal-caption-label',
    WEEKDAY: 'cal-weekday',
    CHEVRON: 'cal-chevron',
    TODAY: 'cal-today',
    OUTSIDE: 'cal-outside',
    RANGE_START: 'cal-range-start',
    RANGE_MIDDLE: 'cal-range-middle',
    RANGE_END: 'cal-range-end',
    SELECTED: 'cal-selected',
    /**
     * Our own custom modifier (not one of react-day-picker's), applied to the sole day of an
     * open-started/open-ended range - i.e. a `selected` day that isn't also `range_start`/`range_end`,
     * which the library only sets when *both* `from` and `to` are defined. Without this, such a day gets
     * no highlight pill at all (see {@link CALENDAR_SELECTORS.OPEN_BOUND} usage in `Calendar.tsx`).
     */
    OPEN_BOUND: 'cal-open-bound',
} as const

// Assigned to DayPicker via its `classNames` prop
export const CALENDAR_CLASS_NAMES: Partial<Record<UI | DayFlag | SelectionState, string>> = {
    [UI.Root]: withOwnClass(UI.Root, CLASSES.ROOT),
    [UI.Day]: withOwnClass(UI.Day, CLASSES.DAY),
    [UI.DayButton]: withOwnClass(UI.DayButton, CLASSES.DAY_BUTTON),
    [UI.MonthCaption]: withOwnClass(UI.MonthCaption, CLASSES.MONTH_CAPTION),
    [UI.CaptionLabel]: withOwnClass(UI.CaptionLabel, CLASSES.CAPTION_LABEL),
    [UI.Weekday]: withOwnClass(UI.Weekday, CLASSES.WEEKDAY),
    [UI.Chevron]: withOwnClass(UI.Chevron, CLASSES.CHEVRON),
    [DayFlag.today]: withOwnClass(DayFlag.today, CLASSES.TODAY),
    [DayFlag.outside]: withOwnClass(DayFlag.outside, CLASSES.OUTSIDE),
    [SelectionState.range_start]: withOwnClass(SelectionState.range_start, CLASSES.RANGE_START),
    [SelectionState.range_middle]: withOwnClass(SelectionState.range_middle, CLASSES.RANGE_MIDDLE),
    [SelectionState.range_end]: withOwnClass(SelectionState.range_end, CLASSES.RANGE_END),
    [SelectionState.selected]: withOwnClass(SelectionState.selected, CLASSES.SELECTED),
}

// Assigned as-is (not via DayPicker's `classNames` prop, which only accepts its own UI/DayFlag/SelectionState
// keys) to DayPicker's `modifiersClassNames` prop, keyed to our custom `openBound` modifier
export const CALENDAR_OPEN_BOUND_CLASS_NAME = CLASSES.OPEN_BOUND

export const CALENDAR_SELECTORS = {
    ROOT: `.${CLASSES.ROOT}`,
    DAY_BUTTON: `.${CLASSES.DAY_BUTTON}`,
    DAY: `.${CLASSES.DAY}`,
    TODAY: `.${CLASSES.TODAY}:not(.${CLASSES.OUTSIDE})`,
    RANGE_START: `.${CLASSES.RANGE_START}`,
    RANGE_MIDDLE: `.${CLASSES.RANGE_MIDDLE}`,
    RANGE_END: `.${CLASSES.RANGE_END}`,
    MONTH_CAPTION: `.${CLASSES.MONTH_CAPTION}`,
    CAPTION_LABEL: `.${CLASSES.CAPTION_LABEL}`,
    WEEKDAY: `.${CLASSES.WEEKDAY}`,
    CHEVRON: `.${CLASSES.CHEVRON}`,
    SELECTED: `.${CLASSES.SELECTED}`,
    OPEN_BOUND: `.${CLASSES.OPEN_BOUND}`,
}

/**
 * Unlike {@link CALENDAR_CLASS_NAMES}, these `--rdp-*` names are react-day-picker's documented public
 * theming API (see its style.css) rather than internal implementation detail, so they're used as-is -
 * there's no stable name to insulate them behind.
 */
export const CALENDAR_CSS_VARIABLES = {
    ACCENT_BACKGROUND_COLOR: '--rdp-accent-background-color',
    DAY_WIDTH: '--rdp-day-width',
    DAY_HEIGHT: '--rdp-day-height',
    DAY_BUTTON_WIDTH: '--rdp-day_button-width',
    DAY_BUTTON_HEIGHT: '--rdp-day_button-height',
    NAV_HEIGHT: '--rdp-nav-height',
    NAV_BUTTON_WIDTH: '--rdp-nav_button-width',
    NAV_BUTTON_HEIGHT: '--rdp-nav_button-height',
    ACCENT_COLOR: '--rdp-accent-color',
    RANGE_START_DATE_BACKGROUND_COLOR: '--rdp-range_start-date-background-color',
    RANGE_END_DATE_BACKGROUND_COLOR: '--rdp-range_end-date-background-color',
    RANGE_MIDDLE_BACKGROUND_COLOR: '--rdp-range_middle-background-color',
    SELECTED_BORDER: '--rdp-selected-border',
    TODAY_COLOR: '--rdp-today-color',
    WEEKDAY_PADDING: '--rdp-weekday-padding',
    WEEKDAY_OPACITY: '--rdp-weekday-opacity',
}
